import React, { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { SITE, waLink } from "@/data/site";

const TRAVEL = [
  { to: "/domestic-tours", label: "Domestic Tours", desc: "Pan-India journeys" },
  { to: "/international-tours", label: "International", desc: "Beyond India" },
  { to: "/pilgrimage-tours", label: "Pilgrimage", desc: "Char Dham & sacred circuits" },
  { to: "/group-tours", label: "Group Tours", desc: "Travel together" },
  { to: "/destinations", label: "Destinations", desc: "Places we know" },
  { to: "/packages", label: "All Journeys", desc: "Curated packages" },
];
const EVENTS = [
  { to: "/events/weddings", label: "Weddings", desc: "Every function, styled" },
  { to: "/events/destination-weddings", label: "Destination Weddings", desc: "Full-scale, end to end" },
  { to: "/events/celebrations", label: "Celebrations", desc: "Birthdays, anniversaries" },
  { to: "/events/puja-religious", label: "Puja & Religious", desc: "Ceremonies & décor" },
  { to: "/events/corporate-events", label: "Corporate Events", desc: "MICE & offsites" },
  { to: "/events", label: "Events Home", desc: "The full vertical" },
];
const SIMPLE = [
  { to: "/corporate-b2b", label: "Corporate & B2B" },
  { to: "/journal", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drawerSub, setDrawerSub] = useState(null);
  const loc = useLocation();
  const dark = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setDrawerSub(null); }, [loc.pathname]);

  const solid = scrolled || !dark;
  const textCol = solid ? "text-[#2A1808]" : "text-white";

  const Trigger = ({ label, items }) => (
    <div className="mega-trigger relative h-full flex items-center">
      <button className={`flex items-center gap-1 text-sm font-medium tracking-wide ${textCol} hover:text-[#F49E1A] transition-colors`}>
        {label} <ChevronDown size={14} />
      </button>
      <div className="mega-panel absolute top-full left-1/2 -translate-x-1/2 pt-4">
        <div className="w-[420px] rounded-md bg-[#FFFBF5] border border-[#553204]/12 shadow-[0_30px_60px_-30px_rgba(42,24,8,0.5)] p-3 grid grid-cols-2 gap-1">
          {items.map((it) => (
            <Link key={it.to} to={it.to} data-testid={`mega-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className="rounded px-3 py-2.5 hover:bg-[#F9EFE1] transition-colors">
              <div className="text-sm font-semibold text-[#2A1808]">{it.label}</div>
              {it.desc && <div className="text-xs text-[#8A6B47]">{it.desc}</div>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <header data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? "bg-[#FFF7EC]/92 backdrop-blur-md border-b border-[#553204]/10 py-2.5" : "bg-transparent py-4"}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
          <img src={SITE.logo} alt="VacanzaGo — Your Travel Guide" className={`h-10 sm:h-11 w-auto ${solid ? "" : "bg-white/90 rounded-md p-1"}`} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 h-12">
          <Trigger label="Travel" items={TRAVEL} />
          <Trigger label="Events" items={EVENTS} />
          {SIMPLE.map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) => `text-sm font-medium tracking-wide transition-colors ${isActive ? "text-[#F49E1A]" : `${textCol} hover:text-[#F49E1A]`}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/packages" data-testid="header-plan-cta" className="hidden sm:inline-flex items-center gap-2 rounded-full btn-orange px-5 py-2.5 text-sm">
            Plan Your Journey
          </Link>
          <button className={`lg:hidden ${textCol}`} onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FFF7EC] border-t border-[#553204]/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4">
            <MobileGroup label="Travel" items={TRAVEL} openSub={drawerSub} setSub={setDrawerSub} />
            <MobileGroup label="Events" items={EVENTS} openSub={drawerSub} setSub={setDrawerSub} />
            {SIMPLE.map((n) => (
              <Link key={n.to} to={n.to} className="block py-3 text-base font-medium border-b border-[#553204]/5 text-[#2A1808]">{n.label}</Link>
            ))}
            <div className="flex gap-3 mt-5">
              <Link to="/packages" className="flex-1 text-center rounded-full btn-orange px-5 py-3">Plan Your Journey</Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center justify-center rounded-full border border-[#553204]/20 px-4 py-3"><MessageCircle size={18} /></a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileGroup({ label, items, openSub, setSub }) {
  const isOpen = openSub === label;
  return (
    <div className="border-b border-[#553204]/5">
      <button onClick={() => setSub(isOpen ? null : label)} className="w-full flex items-center justify-between py-3 text-base font-medium text-[#2A1808]">
        {label} <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="pb-2 pl-3">
          {items.map((it) => (
            <Link key={it.to} to={it.to} className="block py-2 text-sm text-[#553204]">{it.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}
